import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import {InitializeDatasetStepDataName} from 'analysis-tool/AnalysisStepDataNames';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepConfigurationViewTemplate, {
  StepConfigurationViewTemplateProps,
} from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {DataFrame, DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PreviewOutput} from 'pipelines/network/PipelineDAO';
import SearchInput from 'search/inputs/SearchInput';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {
  AsyncState,
  AsyncStatus,
  getMostRecentData,
  NOT_FETCHED_ASYNC_STATE,
} from 'ui-library/hooks/useAsyncState';
import Stepper, {StepperStep} from 'ui-library/stepper/Stepper';

import ChooseDifferentDataFrameCreationMethodButton from '../ChooseDifferentDataFrameCreationMethodButton';
import {getColumnMappingStepperStep} from '../column-mapping/ColumnMappingStepperStep';
import {
  getTemplateDataFrameColumns,
  selectedColumnsToColumnMappings,
} from '../column-mapping/ColumnMappingUtil';
import {DataFrameNameState} from '../DataFrameCreationTypes';
import {getSharedDataFrameCreationMethodProps, openSelectedItemForNewStep} from '../DataFrameCreationUtils';
import DataFrameTablePreview from '../DataFrameTablePreview';
import {useAutoUpdatePreview} from '../hooks/useAutoUpdatePreview';
import DataFrameNameField from '../steps/DataFrameNameField';

const DEFAULT_DATA_FRAME_NAME_STATE: DataFrameNameState = {name: '', hasUserEdits: false};

const datasetIdGlobalState = makeUseGlobalClientState<string | null>(null);
const dataFrameNameGlobalState = makeUseGlobalClientState<DataFrameNameState>(DEFAULT_DATA_FRAME_NAME_STATE);
const isFinalizingGlobalState = makeUseGlobalClientState<boolean>(false);
const previewAsyncGlobalState =
  makeUseGlobalClientState<AsyncState<PreviewOutput<PipelineStepType.DATA_CATALOG_DATASET>>>(
    NOT_FETCHED_ASYNC_STATE
  );

export type CreateDataFrameFromDataCatalogViewProps =
  MainViewComponentProps<AnalysisMainViewItemType.DATA_CATALOG_DATASET_CONFIGURATION>;

export default function CreateDataFrameFromDataCatalogView({
  analysis,
  clientStateId,
  selectedItem,
  onCloseStepInitializationView,
  onOpenStepInitializationView,
}: CreateDataFrameFromDataCatalogViewProps): JSX.Element {
  const [datasetIdState, setDatasetIdState] = datasetIdGlobalState.useGlobalClientState(clientStateId);
  const [selectedColumns, setSelectedColumns] = useState<Array<DataFrameColumnDef | null>>([]);
  const [dataFrameNameState, setDataFrameNameState] =
    dataFrameNameGlobalState.useGlobalClientState(clientStateId);
  const [isFinalizing, setIsFinalizing] = isFinalizingGlobalState.useGlobalClientState(clientStateId);
  const [isManuallyChanged, setIsManuallyChanged] = useState<boolean>(false);

  function isFormComplete(): boolean {
    return datasetIdState != null && dataFrameNameState.name.trim() !== '';
  }

  const [previewState, setPreviewState] = previewAsyncGlobalState.useGlobalClientState(clientStateId);
  useAutoUpdatePreview(
    datasetIdState != null
      ? {
          stepType: PipelineStepType.DATA_CATALOG_DATASET,
          // Data frame name isn't important to actually generate the preview, and keeping it constant ensure
          // we don't regenerate the preview when user is typing a new name
          stepConfig: {datasetId: datasetIdState, dataFrameName: ''},
        }
      : null,
    (data, setAsyncState) => {
      AnalysisActions.runPreviewAnalysisStep<PipelineStepType.DATA_CATALOG_DATASET>(
        data,
        {status: AsyncStatus.NOT_FETCHED},
        setAsyncState
      );
    },
    (nextState: AsyncState<PreviewOutput<PipelineStepType.DATA_CATALOG_DATASET>>) => {
      setPreviewState(nextState);
    }
  );

  const templateDataFrameColumns = useMemo(
    () => getTemplateDataFrameColumns(analysis, selectedItem.stepIdToReplace),
    [analysis, selectedItem.stepIdToReplace]
  );
  const previewDataFrame: DataFrame | null =
    getMostRecentData(previewState)?.dataFrames[InitializeDatasetStepDataName.DATASET][0] ?? null;

  function clearState(): void {
    setDatasetIdState(null);
    setDataFrameNameState(DEFAULT_DATA_FRAME_NAME_STATE);
    setIsFinalizing(false);
  }

  const onCancel: StepConfigurationViewTemplateProps['onCancel'] = (...args) => {
    onCloseStepInitializationView(...args);
    clearState();
  };

  async function onFinalize(): Promise<void> {
    assert(isFormComplete(), 'Tried to finalize with incomplete form');
    setIsFinalizing(true);

    try {
      const newStep = await AnalysisActions.addOrReplaceAndRunAnalysisStep(
        analysis.id,
        {
          stepType: PipelineStepType.DATA_CATALOG_DATASET,
          stepVersion: 0,
          stepConfig: {datasetId: datasetIdState!, dataFrameName: dataFrameNameState.name},
        },
        selectedItem.stepIdToReplace
          ? {
              originalStepId: selectedItem.stepIdToReplace,
              columnMapping: selectedColumnsToColumnMappings(selectedColumns, templateDataFrameColumns),
            }
          : undefined
      );
      openSelectedItemForNewStep(newStep, onCloseStepInitializationView);
    } finally {
      setIsFinalizing(false);
      clearState();
    }
  }

  return (
    <StepConfigurationViewTemplate
      {...getSharedDataFrameCreationMethodProps(selectedItem)}
      clientStateId={clientStateId}
      dataFrames={[]}
      disableFinalizeButton={!isFormComplete()}
      isFinalizing={isFinalizing}
      messages={[]}
      onCancel={onCancel}
      onFinalize={onFinalize}
      renderAdditionalPreviewContent={() => (
        <div className={css(styles.preview)}>
          <DataFrameTablePreview
            clientStateId={clientStateId}
            getDataFrameFromOutput={(previewOutput: PreviewOutput<PipelineStepType.DATA_CATALOG_DATASET>) =>
              previewOutput.dataFrames[InitializeDatasetStepDataName.DATASET][0]
            }
            noPreviewSubtitle="Select a dataset to see a preview of your table."
            previewState={previewState}
          />
        </div>
      )}
      title="Dataset"
      toolbar={
        <ChooseDifferentDataFrameCreationMethodButton
          onOpenStepInitializationView={(newSelectedItem: {
            type: AnalysisMainViewItemType.CHOOSE_DATASET_CREATION_METHOD;
          }) => {
            clearState();
            onOpenStepInitializationView({
              ...newSelectedItem,
              stepIdToReplace: selectedItem.stepIdToReplace,
            });
          }}
        />
      }
    >
      <Stepper>
        <StepperStep title="Select a source dataset">
          <SearchInput
            autoFocus={false}
            disableTyping={datasetIdState != null}
            isMulti={false}
            onRemove={() => {
              setDatasetIdState(null);
            }}
            onSelect={(id) => {
              setDatasetIdState(id);
            }}
            placeholder=""
            selected={datasetIdState == null ? [] : [datasetIdState]}
            types={[ModelTypeConstants.DATASET]}
          />
        </StepperStep>
        {selectedItem.stepIdToReplace != null &&
          getColumnMappingStepperStep({
            templateDataFrameColumns,
            newDataFrameColumns: previewDataFrame?.columns ?? null,
            newDataFrameState: previewState.status,
            selectedColumns,
            setSelectedColumns,
            isManuallyChanged,
            setIsManuallyChanged,
          })}
        <StepperStep title="Name table">
          <DataFrameNameField
            {...dataFrameNameState}
            onNameChange={(newName: string) => {
              setDataFrameNameState({name: newName, hasUserEdits: true});
            }}
          />
        </StepperStep>
      </Stepper>
    </StepConfigurationViewTemplate>
  );
}

const MARGIN = 12;

const styles = StyleSheet.create({
  preview: {
    height: `calc(100% - ${MARGIN * 2}px)`,
    margin: MARGIN,
  },
});
